import { useEffect, useState } from "react";
import {
  Users, FileText, MessageSquare, Award, BookOpen, CheckCircle2, X, Star,
  Plus, Trash2, BarChart3, Handshake, Megaphone, DollarSign, Activity,
  Ban, ShieldCheck, TrendingUp, Download, BookMarked, AlertTriangle, Sparkles,
  Calendar, Compass, Pencil, Globe, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import useSEO from "../../hooks/useSEO";
import ImageUploader from "../../components/ImageUploader";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import BlogBlocksEditor from "../../components/BlogBlocksEditor";

const PROVINCES = ["Québec", "Ontario", "Alberta", "Colombie-Britannique", "Manitoba", "Saskatchewan", "Nouveau-Brunswick", "Nouvelle-Écosse"];

const REQUEST_STATUS = [
  ["nouvelle", "Nouvelle"], ["en_traitement", "En traitement"], ["assignee", "Assignée"], ["terminee", "Terminée"],
];
const ROLE_LABELS = { client: "Client", professional: "Professionnel", entrepreneur: "Entrepreneur", organism: "Organisme", admin: "Admin" };

export default function AdminDashboard() {
  useSEO({ title: "Administration", description: "Tableau de bord d'administration DemkaBiz." });
  const { user } = useAuth();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(0);
  const PAGE_SIZE = 25;
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [guides, setGuides] = useState([]);
  const [resources, setResources] = useState([]);
  const [ads, setAds] = useState([]);
  const [contents, setContents] = useState([]);
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [demoStats, setDemoStats] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);

  const [forms, setForms] = useState({ profile: false, post: false, test: false, partner: false, announcement: false, subscription: false, event: false, guide: false, resource: false, ad: false });
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingGuide, setEditingGuide] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [rejectingProfile, setRejectingProfile] = useState(null);
  const [pendingRoles, setPendingRoles] = useState({}); // { userId: newRole } for unsaved role changes
  const [savingRoleId, setSavingRoleId] = useState(null);
  const openForm = (k) => setForms((f) => ({ ...f, [k]: true }));
  const closeForm = (k) => setForms((f) => ({ ...f, [k]: false }));

  const refresh = async () => {
    const [s, a, p, u, r, c, b, t, pa, an, sub, ev, gu, res, adsData, contentData] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/analytics"),
      api.get("/profiles", { params: { status: "" } }),
      api.get("/admin/users", { params: { skip: usersPage * PAGE_SIZE, limit: PAGE_SIZE, role: userRoleFilter !== "all" ? userRoleFilter : undefined } }),
      api.get("/admin/requests"),
      api.get("/categories"),
      api.get("/admin/blog"),
      api.get("/testimonials"),
      api.get("/admin/partners"),
      api.get("/admin/announcements"),
      api.get("/admin/subscriptions", { params: { skip: subsPage * PAGE_SIZE, limit: PAGE_SIZE } }),
      api.get("/admin/events"),
      api.get("/admin/guides"),
      api.get("/admin/resources"),
      api.get("/admin/ads"),
      api.get("/admin/content"),
    ]);
    setStats(s.data); setAnalytics(a.data); setProfiles(p.data);
    setUsers(u.data.items); setUsersTotal(u.data.total);
    setRequests(r.data);
    setCategories(c.data); setPosts(b.data); setTestimonials(t.data);
    setPartners(pa.data); setAnnouncements(an.data);
    setSubscriptions(sub.data.items); setSubsTotal(sub.data.total);
    setEvents(ev.data); setGuides(gu.data);
    setResources(res.data);
    setAds(adsData.data);
    setContents(contentData.data);
    try {
      const { data: ds } = await api.get("/admin/demo-data-stats");
      setDemoStats(ds);
    } catch { setDemoStats(null); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [usersPage, subsPage, userRoleFilter]);

  // Demo data management
  const cleanupDemoData = async () => {
    if (!window.confirm("Supprimer définitivement TOUTES les données de démonstration ?\n\nCette action est irréversible. Les vraies données utilisateurs ne seront PAS touchées.")) return;
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-demo-data");
      toast.success(`${data.deleted.total} enregistrement(s) de démo supprimé(s).`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur lors du nettoyage");
    } finally { setDemoBusy(false); }
  };
  const reseedDemoData = async () => {
    if (!window.confirm("Réinsérer les 30 enregistrements de démonstration (profils, demandes, témoignages, articles, partenaires) ?")) return;
    setDemoBusy(true);
    try {
      await api.post("/admin/reseed-demo-data");
      toast.success("Données de démonstration réinsérées.");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur lors du re-seed");
    } finally { setDemoBusy(false); }
  };

  // Profile actions
  const setProfileStatus = async (id, status, reason = "") => {
    await api.patch(`/admin/profiles/${id}/status`, { status, reason });
    toast.success(status === "approved" ? "Profil approuvé — email envoyé" : status === "rejected" ? "Profil refusé — email envoyé" : "Statut mis à jour");
    refresh();
  };
  const toggleFeatured = async (p) => { await api.patch(`/admin/profiles/${p.id}/featured`, { featured: !p.featured }); refresh(); };
  const deleteProfile = async (id) => { if (window.confirm("Supprimer ce profil ?")) { await api.delete(`/admin/profiles/${id}`); refresh(); } };
  // Request actions
  const setRequestStatus = async (id, status) => { await api.patch(`/admin/requests/${id}/status`, { status }); refresh(); };
  const assignRequest = async (id, profileId) => {
    if (!profileId) return;
    try {
      const { data } = await api.patch(`/admin/requests/${id}/assign`, { profile_id: profileId });
      toast.success(`Demande attribuée à ${data.profile_name}. Email envoyé.`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur lors de l'attribution");
    }
  };
  // User actions
  const deleteUser = async (id) => { if (window.confirm("Supprimer cet utilisateur ?")) { await api.delete(`/admin/users/${id}`); refresh(); } };
  const banUser = async (u) => { await api.patch(`/admin/users/${u.id}/ban`, { banned: !u.banned }); toast.success(u.banned ? "Compte réactivé" : "Compte suspendu"); refresh(); };
  const changeRole = async (id, role) => {
    setSavingRoleId(id);
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      setPendingRoles((prev) => { const next = { ...prev }; delete next[id]; return next; });
      toast.success("Rôle mis à jour");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSavingRoleId(null);
    }
  };
  // Subscriptions
  const setSubStatus = async (id, status) => { await api.patch(`/admin/subscriptions/${id}`, { status }); refresh(); };
  const deleteSub = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/subscriptions/${id}`); refresh(); } };

  const tabs = [
    { id: "stats", label: "Aperçu", icon: BarChart3 },
    { id: "analytics", label: "Analytics", icon: Activity },
    { id: "subscriptions", label: "Revenus", icon: DollarSign },
    { id: "users", label: "Utilisateurs", icon: Users },
    { id: "profiles", label: "Profils", icon: ShieldCheck },
    { id: "requests", label: "Demandes", icon: FileText },
    { id: "partners", label: "Partenaires", icon: Handshake },
    { id: "announcements", label: "Annonces", icon: Megaphone },
    { id: "categories", label: "Catégories", icon: Award },
    { id: "blog", label: "Blog", icon: BookOpen },
    { id: "events", label: "Événements", icon: Calendar },
    { id: "guides", label: "Guides", icon: Compass },
    { id: "resources", label: "Ressources en ligne", icon: Globe },
    { id: "ads", label: "Publicités", icon: Megaphone },
    { id: "content", label: "Textes", icon: FileText },
    { id: "testimonials", label: "Témoignages", icon: MessageSquare },
  ];

  const filteredUsers = users; // server-side filtered

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Administration</p>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Bonjour {user?.name}</h1>
        <a
          href="/guide-premier-admin-demkabiz.pdf"
          target="_blank"
          rel="noopener noreferrer"
          download
          data-testid="admin-guide-download-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-colors text-sm font-medium shadow-sm self-start"
        >
          <BookMarked className="w-4 h-4" />
          Guide du nouvel admin
          <Download className="w-4 h-4" />
        </a>
      </div>

      <div className="flex gap-1.5 mt-6 border-b border-primary/10 pb-1 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${tab === t.id ? "text-primary border-b-2 border-primary -mb-1" : "text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="w-4 h-4 inline mr-1.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "stats" && stats && (
          <div data-testid="stats-grid">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat label="Utilisateurs" value={stats.users} sub={`${stats.users_clients} clients · ${stats.users_providers} prestataires`} Icon={Users} />
              <Stat label="Revenus totaux" value={`${stats.revenue_total} $`} sub={`${stats.revenue_last_30} $ sur 30j`} Icon={DollarSign} highlight />
              <Stat label="Vues totales" value={stats.page_views_total} sub="depuis le lancement" Icon={TrendingUp} />
              <Stat label="Abonnements actifs" value={stats.active_subscriptions} Icon={Star} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Profils total" value={stats.profiles} sub={`${stats.profiles_pending} en attente`} Icon={ShieldCheck} highlight={stats.profiles_pending > 0} />
              <Stat label="Demandes total" value={stats.requests} sub={`${stats.requests_new} nouvelles · ${stats.requests_in_progress} en cours`} Icon={FileText} highlight={stats.requests_new > 0} />
              <Stat label="Partenaires actifs" value={stats.partners} Icon={Handshake} />
              <Stat label="Annonces actives" value={stats.announcements} Icon={Megaphone} />
            </div>

            {demoStats && (
              <div
                data-testid="demo-data-card"
                className={`mt-8 rounded-2xl border p-6 ${
                  demoStats.total > 0
                    ? "border-amber-300/60 bg-amber-50/50"
                    : "border-emerald-300/60 bg-emerald-50/50"
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {demoStats.total > 0
                      ? <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      : <Sparkles className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    }
                    <div>
                      <h3 className="font-display font-semibold text-base">
                        {demoStats.total > 0 ? "Données de démonstration présentes" : "Base de données propre"}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        {demoStats.total > 0 ? (
                          <>
                            <strong>{demoStats.total}</strong> enregistrement(s) de démo dans la base :{" "}
                            {demoStats.profiles} profil(s) · {demoStats.requests} demande(s) ·{" "}
                            {demoStats.testimonials} témoignage(s) · {demoStats.blog_posts} article(s) ·{" "}
                            {demoStats.partners} partenaire(s).
                            <br />Les vraies données utilisateurs ne seront <strong>pas</strong> touchées par le nettoyage.
                          </>
                        ) : (
                          <>Aucune donnée de démonstration. Vous pouvez réinsérer un jeu d'exemple à tout moment pour tester ou présenter la plateforme.</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                    {demoStats.total > 0 ? (
                      <button
                        onClick={cleanupDemoData}
                        disabled={demoBusy}
                        data-testid="cleanup-demo-data-btn"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-60"
                      >
                        <Trash2 className="w-4 h-4" />
                        {demoBusy ? "Suppression…" : "Nettoyer les données de démo"}
                      </button>
                    ) : (
                      <button
                        onClick={reseedDemoData}
                        disabled={demoBusy}
                        data-testid="reseed-demo-data-btn"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium shadow-sm disabled:opacity-60"
                      >
                        <Sparkles className="w-4 h-4" />
                        {demoBusy ? "Génération…" : "Insérer des données de démo"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DigestPanel />
          </div>
        )}

        {tab === "analytics" && analytics && (
          <AnalyticsTab data={analytics} />
        )}

        {tab === "subscriptions" && (
          <>
            <SubscriptionsTab
              items={subscriptions}
              profiles={profiles}
              users={users}
              onCreate={() => openForm("subscription")}
              onStatus={setSubStatus}
              onDelete={deleteSub}
            />
            <Pagination page={subsPage} total={subsTotal} pageSize={PAGE_SIZE} onChange={setSubsPage} testid="subs-pagination" />
          </>
        )}

        {tab === "users" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">Filtrer par rôle :</span>
              {["all", ...Object.keys(ROLE_LABELS)].map((r) => (
                <button key={r} onClick={() => { setUserRoleFilter(r); setUsersPage(0); }} data-testid={`role-filter-${r}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${userRoleFilter === r ? "bg-primary text-white border-primary" : "border-primary/15"}`}>
                  {r === "all" ? "Tous" : ROLE_LABELS[r]}
                </button>
              ))}
              <span className="text-xs text-muted-foreground ml-auto">{usersTotal} utilisateur(s)</span>
            </div>
            <div className="card-surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Nom</th><th className="text-left p-3">Email</th><th className="text-left p-3">Rôle</th><th className="text-left p-3">Statut</th><th className="text-left p-3">Inscrit</th><th></th></tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const pending = pendingRoles[u.id];
                    const currentRole = pending ?? u.role;
                    const isDirty = pending !== undefined && pending !== u.role;
                    return (
                    <tr key={u.id} className={`border-t border-primary/5 ${u.banned ? "bg-red-50/30" : ""}`} data-testid={`admin-user-${u.id}`}>
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3">{u.email} {u.email_verified && <CheckCircle2 className="w-3 h-3 text-emerald-600 inline ml-1" />}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={currentRole}
                            onChange={(e) => setPendingRoles((prev) => ({ ...prev, [u.id]: e.target.value }))}
                            className="bg-white border border-primary/15 rounded-lg px-2 py-1 text-xs"
                            disabled={u.id === user.id || savingRoleId === u.id}
                            data-testid={`role-${u.id}`}
                          >
                            {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          {isDirty && (
                            <button
                              onClick={() => changeRole(u.id, currentRole)}
                              disabled={savingRoleId === u.id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary text-white text-[11px] font-medium hover:opacity-90 disabled:opacity-50"
                              data-testid={`role-save-${u.id}`}
                              title="Enregistrer le nouveau rôle"
                            >
                              {savingRoleId === u.id ? "…" : "Sauvegarder"}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{u.banned ? <span className="text-red-600 text-xs font-semibold">Suspendu</span> : <span className="text-emerald-700 text-xs font-semibold">Actif</span>}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("fr-CA")}</td>
                      <td className="p-3 text-right">
                        {u.id !== user.id && (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => banUser(u)} title={u.banned ? "Réactiver" : "Suspendre"} className="p-2 hover:bg-amber-50 rounded-lg" data-testid={`ban-${u.id}`}>
                              <Ban className={`w-4 h-4 ${u.banned ? "text-emerald-600" : "text-amber-600"}`} />
                            </button>
                            <button onClick={() => deleteUser(u.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={usersPage} total={usersTotal} pageSize={PAGE_SIZE} onChange={setUsersPage} testid="users-pagination" />
          </div>
        )}

        {tab === "profiles" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => openForm("profile")} className="btn-primary" data-testid="add-profile-btn"><Plus className="w-4 h-4" /> Ajouter un profil</button>
            </div>
            {forms.profile && <ProfileForm categories={categories} onClose={() => closeForm("profile")} onCreated={refresh} />}
            {rejectingProfile && (
              <RejectProfileModal
                profile={rejectingProfile}
                onClose={() => setRejectingProfile(null)}
                onConfirm={async (reason) => { await setProfileStatus(rejectingProfile.id, "rejected", reason); setRejectingProfile(null); }}
              />
            )}
            <div className="card-surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Nom</th><th className="text-left p-3">Type</th><th className="text-left p-3">Statut</th><th className="text-left p-3">Note</th><th className="text-left p-3">Vedette</th><th className="p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr key={p.id} className="border-t border-primary/5" data-testid={`admin-profile-${p.id}`}>
                      <td className="p-3"><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.city}, {p.province}</div></td>
                      <td className="p-3">{p.profile_type}</td>
                      <td className="p-3">
                        <select
                          value={p.status}
                          onChange={(e) => {
                            if (e.target.value === "rejected") { setRejectingProfile(p); }
                            else { setProfileStatus(p.id, e.target.value); }
                          }}
                          data-testid={`status-select-${p.id}`}
                          className="text-xs px-2 py-1 rounded-full border border-primary/15 bg-white outline-none focus:border-primary"
                        >
                          <option value="pending">En attente</option>
                          <option value="approved">Approuvé</option>
                          <option value="rejected">Refusé</option>
                        </select>
                        {p.rejection_reason && p.status === "rejected" && (
                          <div className="text-[10px] text-red-600 mt-1 max-w-[200px] truncate" title={p.rejection_reason}>Raison : {p.rejection_reason}</div>
                        )}
                      </td>
                      <td className="p-3">{p.avg_rating?.toFixed?.(1) || "—"} ({p.review_count})</td>
                      <td className="p-3">
                        <button onClick={() => toggleFeatured(p)} data-testid={`toggle-featured-${p.id}`} title="Étiquette En vedette">
                          <Star className={`w-5 h-5 ${p.featured ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
                        </button>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end">
                          <a href={`/profil/${p.slug || p.id}`} target="_blank" rel="noreferrer" title="Voir la fiche publique" className="p-2 hover:bg-primary/10 rounded-lg" data-testid={`view-profile-${p.id}`}>
                            <ExternalLink className="w-4 h-4 text-primary" />
                          </a>
                          {p.status !== "approved" && (
                            <button onClick={() => setProfileStatus(p.id, "approved")} title="Approuver + email" className="p-2 hover:bg-emerald-50 rounded-lg" data-testid={`approve-${p.id}`}>
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            </button>
                          )}
                          {p.status !== "rejected" && (
                            <button onClick={() => setRejectingProfile(p)} title="Refuser (avec raison)" className="p-2 hover:bg-red-50 rounded-lg" data-testid={`reject-${p.id}`}>
                              <X className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                          <button onClick={() => deleteProfile(p.id)} title="Supprimer" className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-profile-${p.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "requests" && (
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Client</th>
                  <th className="text-left p-3">Catégorie</th>
                  <th className="text-left p-3">Lieu</th>
                  <th className="text-left p-3">Urgence</th>
                  <th className="text-left p-3">Destinataire</th>
                  <th className="text-left p-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const assigned = profiles.find((p) => p.id === r.profile_id);
                  return (
                    <tr key={r.id} className="border-t border-primary/5 align-top" data-testid={`admin-request-${r.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.email}</div>
                        {r.phone && <div className="text-xs text-muted-foreground">{r.phone}</div>}
                        <details className="mt-1">
                          <summary className="text-xs text-primary cursor-pointer hover:underline">Voir la description</summary>
                          <p className="text-xs mt-1 max-w-xs whitespace-pre-wrap text-foreground/80">{r.description}</p>
                        </details>
                      </td>
                      <td className="p-3">{r.category || "—"}</td>
                      <td className="p-3">{r.city || "—"}{r.province ? `, ${r.province}` : ""}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          r.urgency === "urgente" ? "bg-red-100 text-red-700"
                          : r.urgency === "moyenne" ? "bg-amber-100 text-amber-700"
                          : "bg-emerald-100 text-emerald-700"
                        }`}>{r.urgency}</span>
                      </td>
                      <td className="p-3">
                        {assigned ? (
                          <div className="text-xs">
                            <div className="font-medium text-primary">{assigned.name}</div>
                            <div className="text-muted-foreground">{assigned.type}</div>
                          </div>
                        ) : (
                          <select
                            defaultValue=""
                            onChange={(e) => assignRequest(r.id, e.target.value)}
                            data-testid={`request-assign-${r.id}`}
                            className="bg-white border border-primary/15 rounded-lg px-2 py-1 text-xs max-w-[200px]"
                          >
                            <option value="">Attribuer à…</option>
                            {profiles
                              .filter((p) => p.status === "approved")
                              .map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} ({p.type})
                                </option>
                              ))}
                          </select>
                        )}
                      </td>
                      <td className="p-3">
                        <select value={r.status} onChange={(e) => setRequestStatus(r.id, e.target.value)} data-testid={`request-status-${r.id}`} className="bg-white border border-primary/15 rounded-lg px-2 py-1 text-xs">
                          {REQUEST_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
                {requests.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Aucune demande pour le moment.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "partners" && (
          <PartnersTab items={partners} onCreate={() => openForm("partner")} forms={forms} closeForm={closeForm} refresh={refresh} />
        )}
        {tab === "announcements" && (
          <AnnouncementsTab items={announcements} onCreate={() => openForm("announcement")} forms={forms} closeForm={closeForm} refresh={refresh} />
        )}
        {tab === "categories" && <CategoryManager categories={categories} refresh={refresh} />}
        {tab === "blog" && (
          <>
            <div className="flex justify-end mb-4"><button onClick={() => { setEditingPost(null); openForm("post"); }} className="btn-primary" data-testid="add-post-btn"><Plus className="w-4 h-4" /> Nouvel article</button></div>
            {forms.post && <PostForm initial={editingPost} onClose={() => { closeForm("post"); setEditingPost(null); }} onSaved={refresh} />}
            <BlogManager posts={posts} refresh={refresh} onEdit={(p) => { setEditingPost(p); openForm("post"); }} />
          </>
        )}
        {tab === "events" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setEditingEvent(null); openForm("event"); }} className="btn-primary" data-testid="add-event-btn">
                <Plus className="w-4 h-4" /> Nouvel événement
              </button>
            </div>
            {forms.event && <EventForm initial={editingEvent} onClose={() => { closeForm("event"); setEditingEvent(null); }} onSaved={refresh} />}
            <EventManager items={events} refresh={refresh} onEdit={(ev) => { setEditingEvent(ev); openForm("event"); }} />
          </>
        )}
        {tab === "guides" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setEditingGuide(null); openForm("guide"); }} className="btn-primary" data-testid="add-guide-btn">
                <Plus className="w-4 h-4" /> Nouveau guide
              </button>
            </div>
            {forms.guide && <GuideForm initial={editingGuide} onClose={() => { closeForm("guide"); setEditingGuide(null); }} onSaved={refresh} />}
            <GuideManager items={guides} refresh={refresh} onEdit={(g) => { setEditingGuide(g); openForm("guide"); }} />
          </>
        )}
        {tab === "resources" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setEditingResource(null); openForm("resource"); }} className="btn-primary" data-testid="add-resource-btn">
                <Plus className="w-4 h-4" /> Nouvelle ressource
              </button>
            </div>
            {forms.resource && <ResourceForm initial={editingResource} onClose={() => { closeForm("resource"); setEditingResource(null); }} onSaved={refresh} />}
            <ResourceManager items={resources} refresh={refresh} onEdit={(r) => { setEditingResource(r); openForm("resource"); }} />
          </>
        )}
        {tab === "ads" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setEditingAd(null); openForm("ad"); }} className="btn-primary" data-testid="add-ad-btn">
                <Plus className="w-4 h-4" /> Nouvelle publicité
              </button>
            </div>
            {forms.ad && <AdForm initial={editingAd} onClose={() => { closeForm("ad"); setEditingAd(null); }} onSaved={refresh} />}
            <AdManager items={ads} refresh={refresh} onEdit={(a) => { setEditingAd(a); openForm("ad"); }} />
          </>
        )}
        {tab === "content" && (
          <ContentManager items={contents} refresh={refresh} />
        )}
        {tab === "testimonials" && (
          <>
            <div className="flex justify-end mb-4"><button onClick={() => openForm("test")} className="btn-primary" data-testid="add-test-btn"><Plus className="w-4 h-4" /> Nouveau témoignage</button></div>
            {forms.test && <TestForm onClose={() => closeForm("test")} onCreated={refresh} />}
            <TestimonialManager items={testimonials} refresh={refresh} />
          </>
        )}

        {forms.subscription && <SubscriptionForm profiles={profiles} users={users} onClose={() => closeForm("subscription")} onCreated={refresh} />}
      </div>
    </div>
  );
}

function Pagination({ page, total, pageSize, onChange, testid }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-2 mt-4 flex-wrap" data-testid={testid}>
      <span className="text-xs text-muted-foreground">Page {page + 1} / {pages}{` · ${total} éléments`}</span>
      <div className="flex gap-1">
        <button disabled={page === 0} onClick={() => onChange(Math.max(0, page - 1))} className="px-3 py-1.5 text-xs rounded-full border border-primary/15 hover:bg-secondary disabled:opacity-30">← Précédent</button>
        <button disabled={page >= pages - 1} onClick={() => onChange(page + 1)} className="px-3 py-1.5 text-xs rounded-full border border-primary/15 hover:bg-secondary disabled:opacity-30">Suivant →</button>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, Icon, highlight }) {
  return (
    <div className={`card-surface p-5 ${highlight ? "ring-2 ring-primary/20" : ""}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="font-display font-bold text-3xl mt-1">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        {Icon && <div className="w-11 h-11 rounded-2xl bg-secondary flex items-center justify-center text-primary"><Icon className="w-5 h-5" /></div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const m = { approved: ["bg-emerald-100", "text-emerald-800", "Approuvé"], pending: ["bg-amber-100", "text-amber-800", "En attente"], rejected: ["bg-red-100", "text-red-800", "Refusé"], active: ["bg-emerald-100", "text-emerald-800", "Actif"], cancelled: ["bg-stone-100", "text-stone-700", "Annulé"], expired: ["bg-red-100", "text-red-800", "Expiré"] };
  const [bg, tx, label] = m[status] || ["bg-stone-100", "text-stone-800", status];
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${bg} ${tx}`}>{label}</span>;
}

function AnalyticsTab({ data }) {
  const maxView = Math.max(...(data.daily_views || []).map((d) => d.count), 1);
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="card-surface p-6">
        <h3 className="font-display font-semibold text-lg mb-4">Vues sur 30 jours</h3>
        <div className="flex items-end gap-1 h-40">
          {data.daily_views.map((d) => (
            <div key={d.day} className="flex-1 bg-primary/80 hover:bg-primary rounded-t transition-colors" style={{ height: `${(d.count / maxView) * 100}%` }} title={`${d.day}: ${d.count} vues`} />
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-3 text-center">{data.daily_views[0]?.day} → {data.daily_views[data.daily_views.length - 1]?.day}</div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Pages les plus consultées</h3>
          <div className="space-y-2">
            {data.top_pages.length === 0 && <p className="text-sm text-muted-foreground">Pas encore de données.</p>}
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs truncate flex-1">{p.path}</span>
                <span className="font-display font-bold ml-3">{p.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Inscriptions récentes</h3>
          <div className="space-y-2">
            {data.recent_users.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm border-b border-primary/5 pb-2 last:border-0">
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.role}</div>
                </div>
                <div className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("fr-CA")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <h3 className="font-display font-semibold text-lg mb-4">Demandes récentes</h3>
        <div className="space-y-2">
          {data.recent_requests.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-sm border-b border-primary/5 pb-2 last:border-0">
              <div>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.category} · {r.city || "—"}</div>
              </div>
              <StatusBadge status={r.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SubscriptionsTab({ items, onCreate, onStatus, onDelete }) {
  const totalRevenue = items.filter((s) => s.status === "active").reduce((sum, s) => sum + (s.amount || 0), 0);
  return (
    <div data-testid="subscriptions-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Revenu actif total</div>
          <div className="font-display font-bold text-3xl text-primary">{totalRevenue.toFixed(2)} $</div>
        </div>
        <button onClick={onCreate} className="btn-primary" data-testid="add-subscription-btn"><Plus className="w-4 h-4" /> Enregistrer un revenu</button>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left p-3">Libellé</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Montant</th><th className="text-left p-3">Statut</th><th className="text-left p-3">Date</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">Aucun revenu enregistré.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="border-t border-primary/5">
                <td className="p-3"><div className="font-medium">{s.label}</div><div className="text-xs text-muted-foreground">{s.notes}</div></td>
                <td className="p-3">{s.plan}</td>
                <td className="p-3 font-display font-bold">{s.amount} {s.currency}</td>
                <td className="p-3">
                  <select value={s.status} onChange={(e) => onStatus(s.id, e.target.value)} className="bg-white border border-primary/15 rounded-lg px-2 py-1 text-xs">
                    <option value="active">Actif</option><option value="cancelled">Annulé</option><option value="expired">Expiré</option>
                  </select>
                </td>
                <td className="p-3 text-xs">{new Date(s.created_at).toLocaleDateString("fr-CA")}</td>
                <td className="p-3 text-right"><button onClick={() => onDelete(s.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubscriptionForm({ profiles, users, onClose, onCreated }) {
  const [form, setForm] = useState({ label: "", plan: "featured", amount: 0, currency: "CAD", status: "active", profile_id: "", user_id: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (!payload.profile_id) delete payload.profile_id;
      if (!payload.user_id) delete payload.user_id;
      await api.post("/admin/subscriptions", payload);
      toast.success("Revenu enregistré"); onCreated(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal title="Nouveau revenu / abonnement" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Libellé" value={form.label} onChange={(v) => setForm({ ...form, label: v })} required />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label="Plan" value={form.plan} onChange={(v) => setForm({ ...form, plan: v })} options={[["featured", "Profil en vedette"], ["premium", "Premium"], ["commission", "Commission"], ["other", "Autre"]]} />
          <Input label="Montant (CAD)" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
        </div>
        <Select label="Profil lié (optionnel)" value={form.profile_id} onChange={(v) => setForm({ ...form, profile_id: v })} options={[["", "—"], ...profiles.map((p) => [p.id, p.name])]} />
        <textarea rows={2} placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm" />
        <button type="submit" className="btn-primary" data-testid="sub-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

function PartnersTab({ items, onCreate, forms, closeForm, refresh }) {
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const del = async (id) => { if (window.confirm("Supprimer ce partenaire ?")) { await api.delete(`/admin/partners/${id}`); refresh(); } };
  const seedOfficial = async () => {
    if (!window.confirm("Ajouter (ou mettre à jour) les 6 partenaires officiels DemkaBiz (Demka Communication, Demka Foods, Métissage Québec, LRC, Gouvernement du Canada, RCA Groupe) ?")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-official-partners");
      toast.success(`${data.inserted} ajouté(s), ${data.updated} mis à jour.`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally { setSeeding(false); }
  };
  return (
    <div data-testid="partners-tab">
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <button
          onClick={seedOfficial}
          disabled={seeding}
          data-testid="seed-official-partners-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-secondary border border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground transition text-sm font-medium disabled:opacity-60"
        >
          <Sparkles className="w-4 h-4" />
          {seeding ? "Insertion…" : "Insérer les partenaires officiels"}
        </button>
        <button onClick={onCreate} className="btn-primary" data-testid="add-partner-btn"><Plus className="w-4 h-4" /> Ajouter un partenaire</button>
      </div>
      {forms.partner && <PartnerForm onClose={() => closeForm("partner")} onCreated={refresh} />}
      {editing && <PartnerForm initial={editing} onClose={() => setEditing(null)} onCreated={() => { setEditing(null); refresh(); }} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="card-surface p-5" data-testid={`partner-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="font-display font-semibold">{p.name}</div>
                <div className="text-xs text-muted-foreground capitalize">{p.tier} · ordre {p.order}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>{p.active ? "Actif" : "Inactif"}</span>
            </div>
            {p.logo_url && <img src={p.logo_url} alt={p.name} className="h-10 mt-3 object-contain" />}
            {p.description && <p className="text-sm text-muted-foreground mt-2">{p.description}</p>}
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(p)} className="text-xs text-primary hover:underline">Éditer</button>
              <button onClick={() => del(p.id)} className="text-xs text-red-600 hover:underline">Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerForm({ initial, onClose, onCreated }) {
  const [form, setForm] = useState(initial || { name: "", logo_url: "", website: "", tier: "community", order: 0, active: true, description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial) await api.put(`/admin/partners/${initial.id}`, form);
      else await api.post("/admin/partners", form);
      toast.success(initial ? "Partenaire mis à jour" : "Partenaire créé"); onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal title={initial ? "Modifier partenaire" : "Nouveau partenaire"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="partner-name" />
        <ImageUploader value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} label="Logo" testid="partner-logo" />
        <Input label="Site web" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="partner-website" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label="Catégorie" value={form.tier} onChange={(v) => setForm({ ...form, tier: v })} options={[["gold", "Gold"], ["silver", "Silver"], ["bronze", "Bronze"], ["community", "Communauté"]]} />
          <Input label="Ordre d'affichage" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
        </div>
        <textarea rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Actif</label>
        <button type="submit" className="btn-primary" data-testid="partner-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

function AnnouncementsTab({ items, onCreate, forms, closeForm, refresh }) {
  const del = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/announcements/${id}`); refresh(); } };
  return (
    <div data-testid="announcements-tab">
      <div className="flex justify-end mb-4"><button onClick={onCreate} className="btn-primary" data-testid="add-announcement-btn"><Plus className="w-4 h-4" /> Nouvelle annonce</button></div>
      {forms.announcement && <AnnouncementForm onClose={() => closeForm("announcement")} onCreated={refresh} />}
      <div className="space-y-3">
        {items.length === 0 && <p className="text-center text-muted-foreground p-8">Aucune annonce.</p>}
        {items.map((a) => (
          <div key={a.id} className="card-surface p-5 flex items-start justify-between gap-3" data-testid={`announcement-${a.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-semibold ${a.type === "alert" ? "bg-red-100 text-red-800" : a.type === "warning" ? "bg-amber-100 text-amber-800" : a.type === "success" ? "bg-emerald-100 text-emerald-800" : "bg-primary/10 text-primary"}`}>{a.type}</span>
                <span className="text-xs text-muted-foreground">audience : {a.audience}</span>
                {!a.active && <span className="text-xs text-stone-500">(inactif)</span>}
              </div>
              <div className="font-display font-semibold">{a.title}</div>
              <p className="text-sm text-muted-foreground">{a.body}</p>
            </div>
            <button onClick={() => del(a.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnouncementForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", body: "", type: "info", audience: "all", active: true, cta_label: "", cta_url: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/announcements", form); toast.success("Annonce créée"); onCreated(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal title="Nouvelle annonce" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Titre" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
        <textarea rows={3} placeholder="Corps" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={[["info", "Info"], ["success", "Succès"], ["warning", "Avertissement"], ["alert", "Alerte"]]} />
          <Select label="Audience" value={form.audience} onChange={(v) => setForm({ ...form, audience: v })} options={[["all", "Tout le monde"], ["client", "Clients"], ["provider", "Prestataires"], ["admin", "Admins"]]} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="CTA libellé (optionnel)" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} />
          <Input label="CTA URL" value={form.cta_url} onChange={(v) => setForm({ ...form, cta_url: v })} />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
        <button type="submit" className="btn-primary" data-testid="announcement-submit">Publier</button>
      </form>
    </Modal>
  );
}

function ProfileForm({ categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", profile_type: "professional", photo_url: "", province: "", city: "",
    languages: [], categories: [], description: "", phone: "", email: "", whatsapp: "", website: "",
    status: "approved", featured: false,
  });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/profiles", form); toast.success("Profil créé"); onCreated(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal title="Nouveau profil" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-profile-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Nom" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ap-name" />
          <Select label="Type" value={form.profile_type} onChange={(v) => setForm({ ...form, profile_type: v })} options={[["professional", "Professionnel"], ["entrepreneur", "Entrepreneur"], ["organism", "Organisme"]]} testid="ap-type" />
        </div>
        <ImageUploader value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} label="Photo / Logo" testid="ap-photo" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Site web" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
          <Select label="Province" value={form.province} onChange={(v) => setForm({ ...form, province: v })} options={[["", "—"], ...PROVINCES.map((p) => [p, p])]} />
          <Input label="Ville" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <Input label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Input label="WhatsApp" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        </div>
        <textarea rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm" data-testid="ap-desc" />
        <input placeholder="Langues (séparées par virgule)" onChange={(e) => setForm({ ...form, languages: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm" />
        <div>
          <div className="text-xs text-muted-foreground mb-1">Catégories</div>
          <div className="flex flex-wrap gap-1">
            {categories.map((c) => (
              <button type="button" key={c.slug}
                onClick={() => setForm({ ...form, categories: form.categories.includes(c.slug) ? form.categories.filter((x) => x !== c.slug) : [...form.categories, c.slug] })}
                className={`px-2 py-1 text-xs rounded-full border ${form.categories.includes(c.slug) ? "bg-primary text-white border-primary" : "border-primary/15"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Mettre en vedette</label>
        <button type="submit" className="btn-primary" data-testid="ap-submit">Créer</button>
      </form>
    </Modal>
  );
}

function CategoryManager({ categories, refresh }) {
  const [n, setN] = useState({ slug: "", name: "", icon: "Sparkle" });
  const add = async (e) => { e.preventDefault(); await api.post("/categories", n); setN({ slug: "", name: "", icon: "Sparkle" }); refresh(); };
  const del = async (slug) => { if (window.confirm("Supprimer cette catégorie ?")) { await api.delete(`/categories/${slug}`); refresh(); } };
  return (
    <div>
      <form onSubmit={add} className="card-surface p-5 mb-6 flex flex-wrap gap-2" data-testid="cat-form">
        <input placeholder="slug-url" required value={n.slug} onChange={(e) => setN({ ...n, slug: e.target.value })} className="px-3 py-2 border border-primary/15 rounded-lg text-sm flex-1 min-w-[150px]" />
        <input placeholder="Nom affiché" required value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} className="px-3 py-2 border border-primary/15 rounded-lg text-sm flex-1 min-w-[150px]" />
        <input placeholder="Icône Lucide (ex: Home)" value={n.icon} onChange={(e) => setN({ ...n, icon: e.target.value })} className="px-3 py-2 border border-primary/15 rounded-lg text-sm flex-1 min-w-[150px]" />
        <button type="submit" className="btn-primary" data-testid="cat-add">Ajouter</button>
      </form>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="text-left p-3">Slug</th><th className="text-left p-3">Nom</th><th className="text-left p-3">Volet</th><th></th></tr></thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.slug} className="border-t border-primary/5">
                <td className="p-3 font-mono text-xs">{c.slug}</td><td className="p-3">{c.name}</td><td className="p-3 text-xs">{c.volet || "—"}</td>
                <td className="p-3 text-right"><button onClick={() => del(c.slug)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PostForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    slug: initial?.slug || "",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    category: initial?.category || "actualites",
    cover_url: initial?.cover_url || "",
    published: initial?.published ?? true,
    blocks: Array.isArray(initial?.blocks) ? initial.blocks : [],
  }));
  const [saving, setSaving] = useState(false);

  const doSave = async (publishFlag) => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, published: publishFlag, blocks: form.blocks.length ? form.blocks : null };
      if (isEdit) {
        await api.put(`/admin/blog/${initial.id}`, payload);
        toast.success(publishFlag ? "Article publié" : "Brouillon enregistré");
      } else {
        await api.post("/admin/blog", payload);
        toast.success(publishFlag ? "Article publié" : "Brouillon créé");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Modifier l'article" : "Nouvel article"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); doSave(true); }} className="space-y-3" data-testid="blog-form">
        <Input label="Titre *" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="blog-title" />
        <Input label="Slug URL (optionnel — auto-généré si vide)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="blog-slug" />
        <ImageUploader value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} label="Image de couverture" testid="post-cover" />
        <Select label="Catégorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={[["actualites", "Actualités"], ["immigration", "Immigration"], ["entrepreneuriat", "Entrepreneuriat"], ["evenements", "Événements"]]} testid="blog-category" />
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Résumé</label>
          <textarea rows={2} placeholder="Résumé court (affiché dans les listes)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm" data-testid="blog-excerpt" />
        </div>
        <div data-color-mode="light">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Contenu Markdown (fallback — utilisé si aucun bloc n'est ajouté)</label>
          <div className="mt-1 rounded-lg overflow-hidden border border-primary/15">
            <MDEditor
              value={form.content}
              onChange={(v) => setForm({ ...form, content: v || "" })}
              height={280}
              preview="live"
              data-testid="blog-content"
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Astuce : `![légende](url)` pour insérer une image, `## Titre` pour une section.</p>
        </div>
        <BlogBlocksEditor value={form.blocks} onChange={(v) => setForm({ ...form, blocks: v })} />
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-primary/10">
          <button
            type="button"
            onClick={() => doSave(false)}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/20 text-primary hover:bg-primary/5 transition text-sm font-medium disabled:opacity-60"
            data-testid="blog-save-draft"
            title="Enregistrer comme brouillon sans publier"
          >
            {saving ? "…" : "Enregistrer comme brouillon"}
          </button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="blog-submit">
            {saving ? "…" : (isEdit && initial?.published ? "Enregistrer" : "Publier")}
          </button>
          {isEdit && (
            <span className={`ml-auto text-xs px-3 py-1 rounded-full font-semibold ${initial.published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
              État actuel : {initial.published ? "Publié" : "Brouillon"}
            </span>
          )}
        </div>
      </form>
    </Modal>
  );
}
function BlogManager({ posts, refresh, onEdit }) {
  const [filter, setFilter] = useState("all"); // all | published | draft
  const del = async (id) => { if (window.confirm("Supprimer cet article ?")) { await api.delete(`/admin/blog/${id}`); refresh(); } };
  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/blog/${p.id}`, {
        title: p.title, slug: p.slug || "", excerpt: p.excerpt || "", content: p.content || "",
        category: p.category || "actualites", cover_url: p.cover_url || "",
        published: !p.published, blocks: p.blocks || null,
      });
      toast.success(p.published ? "Article dépublié (brouillon)" : "Article publié");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const counts = {
    all: posts.length,
    published: posts.filter((p) => p.published).length,
    draft: posts.filter((p) => !p.published).length,
  };
  const filtered = posts.filter((p) => filter === "all" || (filter === "published" ? p.published : !p.published));
  const tabs = [
    { k: "all", label: "Tous" },
    { k: "published", label: "Publiés" },
    { k: "draft", label: "Brouillons" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" data-testid="blog-filter-tabs">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              filter === t.k ? "bg-primary text-primary-foreground border-primary" : "bg-white text-foreground border-primary/20 hover:border-primary"
            }`}
            data-testid={`blog-filter-${t.k}`}
          >
            {t.label} <span className="ml-1 opacity-70">({counts[t.k]})</span>
          </button>
        ))}
      </div>
      <div className="card-surface overflow-x-auto" data-testid="blog-list">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left p-3">Titre</th><th className="text-left p-3">Catégorie</th><th className="text-left p-3">Statut</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground text-sm">Aucun article dans cette vue.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className={`border-t border-primary/5 ${!p.published ? "bg-amber-50/20" : ""}`} data-testid={`blog-row-${p.id}`}>
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3">{p.category}</td>
                <td className="p-3 text-xs">
                  {p.published
                    ? <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold" data-testid={`blog-status-${p.id}`}>Publié</span>
                    : <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold" data-testid={`blog-status-${p.id}`}>Brouillon</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button
                      onClick={() => togglePublish(p)}
                      className="px-2 py-1 rounded-lg text-[11px] font-medium border border-primary/20 hover:bg-primary hover:text-white transition"
                      data-testid={`blog-toggle-${p.id}`}
                      title={p.published ? "Repasser en brouillon" : "Publier maintenant"}
                    >
                      {p.published ? "Dépublier" : "Publier"}
                    </button>
                    <button onClick={() => onEdit(p)} className="p-2 hover:bg-primary/10 rounded-lg" data-testid={`blog-edit-${p.id}`} title="Modifier">
                      <Pencil className="w-4 h-4 text-primary" />
                    </button>
                    <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`blog-delete-${p.id}`} title="Supprimer">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TestForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ author: "", role: "", content: "", rating: 5, photo_url: "", approved: true });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/testimonials", form); toast.success("Témoignage créé"); onCreated(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal title="Nouveau témoignage" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Input label="Auteur" required value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
        <Input label="Rôle / Ville" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
        <ImageUploader value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} label="Photo" testid="test-photo" />
        <textarea required rows={4} placeholder="Contenu" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full px-3 py-2 border border-primary/15 rounded-lg text-sm" />
        <Select label="Note" value={String(form.rating)} onChange={(v) => setForm({ ...form, rating: parseInt(v, 10) })} options={[["5", "5"], ["4", "4"], ["3", "3"], ["2", "2"], ["1", "1"]]} />
        <button type="submit" className="btn-primary">Publier</button>
      </form>
    </Modal>
  );
}
function TestimonialManager({ items, refresh }) {
  const del = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/testimonials/${id}`); refresh(); } };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((t) => (
        <div key={t.id} className="card-surface p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">{t.author}</div>
            <button onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-red-600" /></button>
          </div>
          <div className="text-xs text-muted-foreground">{t.role}</div>
          <p className="text-sm mt-2">{t.content}</p>
        </div>
      ))}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full my-4 sm:my-8 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-primary/10 flex-shrink-0">
          <h3 className="font-display font-semibold text-base sm:text-lg">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      <input type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary" />
    </div>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}


// ---------------- EVENTS ----------------
function EventManager({ items, refresh, onEdit }) {
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Événement supprimé");
    refresh();
  };
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-primary/20 p-8 text-center text-muted-foreground" data-testid="events-empty">
        Aucun événement. Cliquez sur « Nouvel événement » pour en créer un.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="events-list">
      {items.map((e) => (
        <div key={e.id} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`event-row-${e.slug}`}>
          {e.poster_url && <img src={e.poster_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${e.status === "upcoming" ? "bg-primary/10 text-primary" : "bg-muted-foreground/10 text-muted-foreground"}`}>
                {e.status === "upcoming" ? "À venir" : "Passé"}
              </span>
              {e.acronym && <span className="text-xs text-primary font-semibold">{e.acronym}</span>}
              {!e.published && <span className="text-[10px] uppercase text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Brouillon</span>}
            </div>
            <div className="font-display font-semibold text-sm mt-1 truncate">{e.title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{e.date_label} · {e.city} · /{e.slug}</div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <a href={`/agenda/${e.slug}`} target="_blank" rel="noreferrer" className="btn-outline text-xs px-3 py-1.5" data-testid={`event-view-${e.slug}`}>Voir</a>
            <button onClick={() => onEdit(e)} className="btn-outline text-xs px-3 py-1.5" data-testid={`event-edit-${e.slug}`} title="Modifier">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => remove(e.id)} className="btn-outline text-xs px-3 py-1.5 text-red-600 border-red-200 hover:bg-red-50" data-testid={`event-delete-${e.slug}`} title="Supprimer">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_EVENT = {
  slug: "", title: "", acronym: "", date_label: "", city: "",
  description: "", long_description: "", tag: "Événement",
  poster_url: "", external_url: "", status: "upcoming", order: 0, published: true,
};

function EventForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? { ...EMPTY_EVENT, ...initial } : EMPTY_EVENT);
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, external_url: form.external_url || null };
      if (isEdit) {
        await api.put(`/admin/events/${initial.id}`, payload);
        toast.success("Événement modifié");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Événement créé");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };
  return (
    <Modal title={isEdit ? "Modifier l'événement" : "Nouvel événement"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="event-form">
        <Input label="Titre *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="event-title" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Slug (URL) *" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required testid="event-slug" />
          <Input label="Acronyme" value={form.acronym} onChange={(v) => setForm({ ...form, acronym: v })} testid="event-acronym" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Date (libellé) *" value={form.date_label} onChange={(v) => setForm({ ...form, date_label: v })} required testid="event-date" />
          <Input label="Ville" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="event-city" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description courte *</label>
          <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="event-description"
            className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description longue (page détail)</label>
          <textarea value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} rows={5} data-testid="event-long-description"
            className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Tag" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} testid="event-tag" />
          <Select label="Statut" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={[["upcoming", "À venir"], ["past", "Passé"]]} testid="event-status" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Affiche</label>
          <ImageUploader value={form.poster_url} onChange={(url) => setForm({ ...form, poster_url: url })} label="Téléverser l'affiche" testid="event-poster-upload" />
        </div>
        <Input label="URL externe (optionnel)" value={form.external_url || ""} onChange={(v) => setForm({ ...form, external_url: v })} testid="event-external" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="event-published" />
          Publié (visible du public)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" className="btn-primary" data-testid="event-form-submit">{isEdit ? "Enregistrer" : "Créer"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- GUIDES ----------------
function GuideManager({ items, refresh, onEdit }) {
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce guide ?")) return;
    await api.delete(`/admin/guides/${id}`);
    toast.success("Guide supprimé");
    refresh();
  };
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-primary/20 p-8 text-center text-muted-foreground" data-testid="guides-empty">
        Aucun guide. Cliquez sur « Nouveau guide » pour en créer un.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="guides-list">
      {items.map((g) => (
        <div key={g.id} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`guide-row-${g.slug}`}>
          {g.cover_url && <img src={g.cover_url} alt="" className="w-20 h-24 rounded-lg object-cover flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm truncate">{g.title}</div>
            {g.edition && <div className="text-xs text-accent font-medium mt-0.5">{g.edition}</div>}
            <div className="text-xs text-muted-foreground mt-0.5">/{g.slug} · {g.pdf_url ? "PDF disponible" : "Sans PDF"}</div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <a href={`/guide-pratique/${g.slug}`} target="_blank" rel="noreferrer" className="btn-outline text-xs px-3 py-1.5" data-testid={`guide-view-${g.slug}`}>Voir</a>
            {g.pdf_url && (
              <a href={g.pdf_url} target="_blank" rel="noreferrer" download className="btn-outline text-xs px-3 py-1.5" data-testid={`guide-pdf-${g.slug}`} title="Télécharger le PDF">
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
            <button onClick={() => onEdit(g)} className="btn-outline text-xs px-3 py-1.5" data-testid={`guide-edit-${g.slug}`} title="Modifier">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => remove(g.id)} className="btn-outline text-xs px-3 py-1.5 text-red-600 border-red-200 hover:bg-red-50" data-testid={`guide-delete-${g.slug}`} title="Supprimer">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_GUIDE = {
  slug: "", title: "", subtitle: "", description: "", long_description: "",
  category: "general", cover_url: "", pdf_url: "", edition: "", order: 0, published: true,
};

function GuideForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? { ...EMPTY_GUIDE, ...initial } : EMPTY_GUIDE);
  const [pdfUploading, setPdfUploading] = useState(false);

  const uploadPdf = async (file) => {
    if (!file) return;
    setPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, pdf_url: data.url }));
      toast.success("PDF téléversé");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur upload PDF");
    } finally { setPdfUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await api.put(`/admin/guides/${initial.id}`, form);
        toast.success("Guide modifié");
      } else {
        await api.post("/admin/guides", form);
        toast.success("Guide créé");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };
  return (
    <Modal title={isEdit ? "Modifier le guide" : "Nouveau guide"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="guide-form">
        <Input label="Titre *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="guide-title" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Slug (URL) *" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required testid="guide-slug" />
          <Input label="Sous-titre" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} testid="guide-subtitle" />
        </div>
        <Input label="Édition (ex: Édition automne 2026)" value={form.edition} onChange={(v) => setForm({ ...form, edition: v })} testid="guide-edition" />
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description courte *</label>
          <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="guide-description"
            className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description longue</label>
          <textarea value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} rows={5} data-testid="guide-long-description"
            className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Couverture</label>
          <ImageUploader value={form.cover_url} onChange={(url) => setForm({ ...form, cover_url: url })} label="Téléverser la couverture" testid="guide-cover-upload" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">PDF téléchargeable</label>
          {form.pdf_url ? (
            <div className="mt-1 flex items-center gap-2 p-2 rounded-lg bg-secondary/60 text-xs">
              <Download className="w-4 h-4 text-primary" />
              <a href={form.pdf_url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1">{form.pdf_url}</a>
              <button type="button" onClick={() => setForm({ ...form, pdf_url: "" })} className="text-red-600" data-testid="guide-pdf-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <input type="file" accept="application/pdf" onChange={(e) => uploadPdf(e.target.files?.[0])} data-testid="guide-pdf-upload"
              className="mt-1 w-full text-xs" disabled={pdfUploading} />
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="guide-published" />
          Publié (visible du public)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" className="btn-primary" data-testid="guide-form-submit">{isEdit ? "Enregistrer" : "Créer"}</button>
        </div>
      </form>
    </Modal>
  );
}



// ---------------- RESOURCES CRUD ----------------
const RESOURCE_CATEGORIES = [
  "Immigration & citoyenneté",
  "Emploi & entrepreneuriat",
  "Santé",
  "Éducation & formation",
  "Organismes d'accueil",
  "Institutions & gouvernement",
  "Provinces & territoires (hors Québec)",
  "Municipalités & régionalisation",
  "Communication & médias",
  "Protection & droits",
];

function ResourceManager({ items, refresh, onEdit }) {
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette ressource ?")) return;
    await api.delete(`/admin/resources/${id}`);
    toast.success("Ressource supprimée");
    refresh();
  };
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-primary/20 p-8 text-center text-muted-foreground" data-testid="resources-empty">
        Aucune ressource. Cliquez sur « Nouvelle ressource » pour en ajouter.
      </div>
    );
  }
  // Group by category
  const groups = items.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});
  return (
    <div className="space-y-6" data-testid="resources-list">
      {Object.entries(groups).map(([cat, arr]) => (
        <div key={cat}>
          <h3 className="font-display font-semibold text-sm uppercase tracking-widest text-primary mb-2">{cat} ({arr.length})</h3>
          <div className="space-y-2">
            {arr.map((r) => (
              <div key={r.id} className="card-surface p-3 flex items-center gap-3" data-testid={`resource-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{r.name}</div>
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline truncate block">{r.url}</a>
                  {r.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{r.description}</div>}
                  {!r.published && <span className="text-[10px] uppercase text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Brouillon</span>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => onEdit(r)} className="p-2 hover:bg-primary/10 rounded-lg" data-testid={`resource-edit-${r.id}`} title="Modifier">
                    <Pencil className="w-4 h-4 text-primary" />
                  </button>
                  <button onClick={() => remove(r.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`resource-delete-${r.id}`} title="Supprimer">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_RESOURCE = { category: RESOURCE_CATEGORIES[0], name: "", url: "", description: "", icon: "globe", order: 0, published: true };

function ResourceForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? { ...EMPTY_RESOURCE, ...initial } : EMPTY_RESOURCE);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await api.put(`/admin/resources/${initial.id}`, form);
        toast.success("Ressource modifiée");
      } else {
        await api.post("/admin/resources", form);
        toast.success("Ressource créée");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };
  return (
    <Modal title={isEdit ? "Modifier la ressource" : "Nouvelle ressource"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="resource-form">
        <Select label="Catégorie *" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={RESOURCE_CATEGORIES.map((c) => [c, c])} testid="resource-category" />
        <Input label="Nom *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="resource-name" />
        <Input label="URL *" value={form.url} onChange={(v) => setForm({ ...form, url: v })} required testid="resource-url" />
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Description courte</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="resource-description"
            className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="resource-published" />
          Publiée (visible du public)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" className="btn-primary" data-testid="resource-form-submit">{isEdit ? "Enregistrer" : "Créer"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- REJECT PROFILE MODAL ----------------
function RejectProfileModal({ profile, onClose, onConfirm }) {
  const [reason, setReason] = useState(profile.rejection_reason || "");
  return (
    <Modal title={`Refuser le profil « ${profile.name} »`} onClose={onClose}>
      <div className="space-y-4" data-testid="reject-profile-modal">
        <p className="text-sm text-muted-foreground">
          Indiquez la raison du refus. Elle sera envoyée par email à l'utilisateur pour qu'il puisse
          corriger son profil et le soumettre à nouveau.
        </p>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Raison du refus *</label>
          <textarea
            required
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            data-testid="reject-reason"
            className="w-full mt-1 px-3 py-2 border border-primary/15 rounded-lg text-sm outline-none focus:border-primary"
            placeholder="Ex : Les coordonnées de contact ne sont pas valides, veuillez les mettre à jour."
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline" data-testid="reject-cancel">Annuler</button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-red-600 text-white hover:bg-red-700 text-sm font-medium transition"
            data-testid="reject-confirm"
          >
            <X className="w-4 h-4" /> Refuser et notifier
          </button>
        </div>
      </div>
    </Modal>
  );
}


// ---------------- AD SLOTS CRUD ----------------
const AD_PLACEMENTS = [
  ["home_leaderboard", "Home — Bandeau leaderboard 970×250"],
  ["home_video", "Home — Vidéo 16:9"],
  ["blog_rectangle", "Blog — Rectangle medium"],
  ["blog_horizontal", "Blog — Bannière horizontale"],
];

function AdManager({ items, refresh, onEdit }) {
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette publicité ?")) return;
    await api.delete(`/admin/ads/${id}`);
    toast.success("Publicité supprimée");
    refresh();
  };
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-primary/20 p-8 text-center text-muted-foreground" data-testid="ads-empty">
        Aucune publicité. Cliquez sur « Nouvelle publicité » pour en ajouter.
      </div>
    );
  }
  const placementLabel = (p) => AD_PLACEMENTS.find(([k]) => k === p)?.[1] || p;
  return (
    <div className="space-y-3" data-testid="ads-list">
      {items.map((a) => (
        <div key={a.id} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`ad-row-${a.id}`}>
          {a.image_url ? (
            <img src={a.image_url} alt="" className="w-32 h-16 rounded-lg object-cover flex-shrink-0 bg-secondary" />
          ) : a.video_url ? (
            <div className="w-32 h-16 rounded-lg bg-black text-white text-xs flex items-center justify-center flex-shrink-0">Vidéo</div>
          ) : null}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">{placementLabel(a.placement)}</div>
            <div className="font-medium text-sm mt-0.5 truncate">{a.title || "(sans titre)"}</div>
            {a.target_url && <a href={a.target_url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground truncate block hover:underline">{a.target_url}</a>}
            {!a.active && <span className="inline-block mt-1 text-[10px] uppercase text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Inactive</span>}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => onEdit(a)} className="p-2 hover:bg-primary/10 rounded-lg" data-testid={`ad-edit-${a.id}`} title="Modifier">
              <Pencil className="w-4 h-4 text-primary" />
            </button>
            <button onClick={() => remove(a.id)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`ad-delete-${a.id}`} title="Supprimer">
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_AD = { placement: "home_leaderboard", title: "", image_url: "", video_url: "", target_url: "", active: true };

function AdForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? { ...EMPTY_AD, ...initial } : EMPTY_AD);
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, image_url: form.image_url || null, video_url: form.video_url || null, target_url: form.target_url || null };
      if (isEdit) {
        await api.put(`/admin/ads/${initial.id}`, payload);
        toast.success("Publicité modifiée");
      } else {
        await api.post("/admin/ads", payload);
        toast.success("Publicité créée");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };
  const isVideo = form.placement === "home_video";
  return (
    <Modal title={isEdit ? "Modifier la publicité" : "Nouvelle publicité"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="ad-form">
        <Select label="Emplacement *" value={form.placement} onChange={(v) => setForm({ ...form, placement: v })} options={AD_PLACEMENTS} testid="ad-placement" />
        <Input label="Titre (interne)" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="ad-title" />
        {isVideo ? (
          <Input label="URL vidéo embed (YouTube: https://www.youtube.com/embed/ID) *" value={form.video_url || ""} onChange={(v) => setForm({ ...form, video_url: v })} required testid="ad-video-url" />
        ) : (
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Image {form.placement === "home_leaderboard" ? "(970×250 recommandé)" : form.placement === "blog_rectangle" ? "(300×250 recommandé)" : "(728×90 recommandé)"}
            </label>
            <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} label="Téléverser la bannière" testid="ad-image-upload" />
          </div>
        )}
        <Input label="URL de destination (au clic)" value={form.target_url || ""} onChange={(v) => setForm({ ...form, target_url: v })} testid="ad-target-url" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="ad-active" />
          Active (visible du public)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" className="btn-primary" data-testid="ad-form-submit">{isEdit ? "Enregistrer" : "Créer"}</button>
        </div>
      </form>
    </Modal>
  );
}



// ---------------- CONTENT MANAGER (Textes des pages) ----------------
function ContentManager({ items, refresh }) {
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-primary/20 p-8 text-center text-muted-foreground" data-testid="content-empty">
        Aucun texte configurable. Le backend seedera les textes par défaut (Mission, Vision) au prochain redémarrage.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="content-list">
      <p className="text-sm text-muted-foreground">
        Modifiez les textes clés de l'application. Les changements sont visibles immédiatement sur le site public.
      </p>
      {items.map((c) => (
        <ContentEditor key={c.key} item={c} refresh={refresh} />
      ))}
    </div>
  );
}

function ContentEditor({ item, refresh }) {
  const [value, setValue] = useState(item.value || "");
  const [saving, setSaving] = useState(false);
  const dirty = value !== (item.value || "");
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${item.key}`, { value, label: item.label });
      toast.success("Texte enregistré — visible immédiatement sur le site");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="card-surface p-4" data-testid={`content-editor-${item.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-display font-semibold text-sm">{item.label || item.key}</div>
          <code className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">{item.key}</code>
        </div>
        {item.updated_at && (
          <span className="text-[10px] text-muted-foreground">
            Modifié le {new Date(item.updated_at).toLocaleDateString("fr-CA")}
          </span>
        )}
      </div>
      <div data-color-mode="light" className="mt-2" data-testid={`content-input-${item.key}`}>
        <MDEditor
          value={value}
          onChange={(v) => setValue(v || "")}
          height={260}
          preview="live"
          textareaProps={{ placeholder: "Tapez votre texte ici. Utilisez la barre d'outils pour formater (gras, italique, liens, listes…). L'aperçu à droite montre le rendu final." }}
        />
      </div>
      <div className="flex justify-end gap-2 mt-2">
        {dirty && (
          <button onClick={() => setValue(item.value || "")} className="btn-outline text-xs px-3 py-1.5" data-testid={`content-reset-${item.key}`}>
            Annuler
          </button>
        )}
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary text-xs px-4 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`content-save-${item.key}`}
        >
          {saving ? "Enregistrement…" : dirty ? "Enregistrer" : "Enregistré"}
        </button>
      </div>
    </div>
  );
}


function DigestPanel() {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const loadRuns = () => api.get("/admin/digest/runs").then((r) => setRuns(r.data || [])).catch(() => {});
  useEffect(() => { loadRuns(); }, []);

  const sendNow = async () => {
    if (!window.confirm("Envoyer le digest hebdo maintenant à tous les admins ?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      if (data.ok) toast.success(`Digest envoyé à ${data.recipients} admin(s)`);
      else toast.error(data.error || "Erreur");
      loadRuns();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  const openPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/digest/preview");
      setPreview(data);
      setShowPreview(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-primary/15 bg-white p-6" data-testid="digest-panel">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Megaphone className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-base">Digest hebdomadaire des admins</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Chaque <strong>lundi 08:00 (America/Toronto)</strong>, un récapitulatif est envoyé automatiquement à tous les administrateurs :
              profils en attente, demandes non traitées, nouveaux inscrits de la semaine, et témoignages en attente d'approbation.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
          <button onClick={openPreview} disabled={busy} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/20 text-primary hover:bg-primary hover:text-white transition text-sm font-medium disabled:opacity-60" data-testid="digest-preview-btn">
            Aperçu
          </button>
          <button onClick={sendNow} disabled={busy} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground hover:opacity-90 transition text-sm font-medium disabled:opacity-60" data-testid="digest-send-btn">
            Envoyer maintenant
          </button>
        </div>
      </div>

      {runs.length > 0 && (
        <div className="mt-5 border-t border-primary/10 pt-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Dernières exécutions</p>
          <ul className="space-y-1 text-sm" data-testid="digest-runs">
            {runs.slice(0, 5).map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 text-muted-foreground">
                <span className="text-foreground font-medium">{new Date(r.sent_at).toLocaleString("fr-CA")}</span>
                <span>·</span>
                <span>{r.recipients} destinataire(s)</span>
                <span>·</span>
                <span className="text-xs">
                  {r.counts?.pending_profiles} profils · {r.counts?.open_requests} demandes · {r.counts?.new_users} nouveaux · {r.counts?.pending_testimonials} témoignages
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showPreview && preview && (
        <Modal title="Aperçu du digest hebdomadaire" onClose={() => setShowPreview(false)}>
          <div className="rounded-xl border border-primary/10 max-h-[70vh] overflow-y-auto bg-white" data-testid="digest-preview-html">
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </Modal>
      )}
    </div>
  );
}

